import { useState } from 'react';
import { Button, Input, Modal, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FileSpreadsheet, Printer, Scale } from 'lucide-react';
import Layout from '../../Layout';
import { printHtml } from '../Treatment/print';
import { shiftHtml } from './ktCounter';
import { dmy, fmtMoney, type Shift } from './ktLogic';
import { ktApi, useKt } from './ktStore';
import { DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';
import { exportShift, STATUS, txnColumns } from './Shift';

/** Kế toán nội bộ nhận biên bản chốt ca và bảng kê của quầy; nhận xong mới khóa được sổ ngày */
export default function KeToanShiftReview() {
  const s = useKt();
  const [reopening, setReopening] = useState<Shift | null>(null);
  const [reason, setReason] = useState('');
  const waiting = s.shifts.filter(x => x.status === 'closed');
  const lech = s.shifts.filter(x => x.status !== 'reopened' && x.diff);

  const columns: ColumnsType<Shift> = [
    { title: 'Số ca', dataIndex: 'no', width: 100 },
    { title: 'Ca', key: 'n', width: 150, render: (_, x) => <div><div>{x.name}</div><div className="text-[11px] text-gray-500">{dmy(x.date)}, chốt lúc {dayjs(x.at).format('HH:mm')}</div></div> },
    { title: 'Người chốt', dataIndex: 'by', width: 180 },
    { title: 'Tiền mặt theo sổ', dataIndex: 'cashExpected', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Kiểm kê', dataIndex: 'cashCounted', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Chênh lệch', key: 'd', width: 200, render: (_, x) => (x.diff ? <div><b className="text-rose-600">{fmtMoney(x.diff)}</b><div className="text-[11px] text-gray-500">{x.diffReason}</div></div> : <span className="text-emerald-700">Khớp</span>) },
    { title: 'Trạng thái', key: 's', width: 180, render: (_, x) => <div><Tag color={STATUS[x.status].color}>{STATUS[x.status].label}</Tag>{x.reopenReason && <div className="text-[11px] text-gray-500">{x.reopenedBy}: {x.reopenReason}</div>}</div> },
    { title: '', key: 'a', width: 210, align: 'right', render: (_, x) => (
      <div className="flex justify-end gap-1">
        <Tooltip title="In biên bản và bảng kê"><Button size="small" type="text" icon={<Printer size={14} />} aria-label={`In ${x.no}`} onClick={() => printHtml(`Biên bản chốt ca ${x.no}`, 'A4', shiftHtml(x))} /></Tooltip>
        <Tooltip title="Xuất Excel"><Button size="small" type="text" icon={<FileSpreadsheet size={14} />} aria-label={`Excel ${x.no}`} onClick={() => exportShift(x)} /></Tooltip>
        {s.tier === 'revenue' && x.status === 'closed' && <Button size="small" type="primary" onClick={() => { const r = ktApi.receiveShift(x.id); r.ok ? message.success('Đã nhận biên bản') : showErrors(r.errors); }}>Nhận</Button>}
        {s.tier === 'revenue' && x.status !== 'reopened' && !x.txns.some(y => s.locks[y.date]) && <Button size="small" onClick={() => { setReason(''); setReopening(x); }}>Mở lại</Button>}
      </div>
    ) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Nhận biên bản ca" desc="Kiểm tra biên bản chốt ca và bảng kê Ngân hàng, Payoo của quầy. Muốn quầy sửa bill trong ca đã chốt thì mở lại ca." />
        <DemoBar />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Kpi icon={<FileSpreadsheet size={20} />} tone="bg-amber-50 text-amber-600" label="Biên bản chờ nhận" value={waiting.length} hint="Nhận xong mới khóa được sổ ngày" />
          <Kpi icon={<Scale size={20} />} tone="bg-rose-50 text-rose-600" label="Ca lệch tiền mặt" value={lech.length} hint={fmtMoney(lech.reduce((t, x) => t + x.diff, 0))} />
        </div>
        <Panel title={`Biên bản chốt ca (${s.shifts.length})`}>
          <Table rowKey="id" size="middle" columns={columns} dataSource={s.shifts} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 1250 }} locale={{ emptyText: 'Chưa có ca nào được chốt' }}
            expandable={{ expandedRowRender: x => <Table rowKey="id" size="small" columns={txnColumns} dataSource={x.txns} pagination={false} /> }} />
        </Panel>
      </div>

      <Modal open={!!reopening} title={`Mở lại ${reopening?.no}`} okText="Mở lại" okButtonProps={{ danger: true }} cancelText="Quay lại" onCancel={() => setReopening(null)} destroyOnHidden
        onOk={() => { const r = ktApi.reopenShift(reopening!.id, reason); if (r.ok) { message.success('Đã mở lại, giao dịch về ca chốt lại'); setReopening(null); } else showErrors(r.errors); }}>
        <p className="text-xs text-gray-500">Biên bản cũ được giữ nguyên để truy vết. Quầy sửa bill xong phải chốt lại.</p>
        <label htmlFor="kt-sh-reopen" className="text-sm font-medium">Lý do</label>
        <Input.TextArea id="kt-sh-reopen" rows={2} value={reason} onChange={e => setReason(e.target.value)} className="!mt-1" />
      </Modal>
    </Layout>
  );
}
